<template>
  <div class="user-group-detail-container">
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-info"></i>
          <span>{{ $t('userManagement.groupDetail') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button @click="handleBack" size="small">
            <i class="el-icon-back"></i> {{ $t('common.back') }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 分组基本信息 -->
    <el-card class="user-group-detail-info" shadow="hover">
      <div class="group-header-content">
        <div class="group-icon-wrapper">
          <i class="el-icon-folder group-icon"></i>
        </div>
        <div class="group-main-info">
          <div class="name-row">
            <h2 class="group-name-text">{{ currentGroup.name }}</h2>
            <el-button type="warning" @click="handleEdit" size="mini" plain round icon="el-icon-edit">
              {{ $t('common.edit') }}
            </el-button>
          </div>
          <div class="group-desc-text">{{ currentGroup.description || $t('userManagement.noDescription') }}</div>
          <div class="group-meta-info">
            <div class="meta-info-item">
              <i class="el-icon-user"></i>
              <span class="label-text">{{ $t('userManagement.memberCount') }}:</span>
              <span class="value-text">
                <el-tag type="primary" size="mini" effect="plain">{{ currentGroup.user_ids ? currentGroup.user_ids.length : 0 }}</el-tag>
              </span>
            </div>
            <div class="meta-info-divider"></div>
            <div class="meta-info-item">
              <i class="el-icon-time"></i>
              <span class="label-text">{{ $t('common.createdAt') }}:</span>
              <span class="value-text">{{ formatDate(currentGroup.createDate) }}</span>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 分组用户列表 -->
    <el-card class="user-group-detail-users" shadow="hover">
      <template slot="header">
        <div class="card-header">
          <span>{{ $t('userManagement.groupMembers') }}</span>
          <el-button type="primary" @click="handleAddUsers" size="small">
            <i class="el-icon-plus"></i> {{ $t('userManagement.addUsers') }}
          </el-button>
        </div>
      </template>
      
      <!-- 用户列表 -->
      <el-table
        :data="groupUsers"
        stripe
        height="500"
        style="width: 100%"
        @selection-change="handleUserSelectionChange"
        v-loading="loading"
        :element-loading-text="$t('common.loading')"
      >
        <el-table-column type="selection" width="55"></el-table-column>
        <el-table-column prop="username" :label="$t('userManagement.username')" width="120"></el-table-column>
        <el-table-column prop="name" :label="$t('userManagement.name')"></el-table-column>
        <el-table-column prop="email" :label="$t('userManagement.email')"></el-table-column>
        <el-table-column prop="mobile" :label="$t('userManagement.phone')"></el-table-column>
        <el-table-column prop="status" :label="$t('userManagement.status')">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 0 ? 'info' : scope.row.status === 2 ? 'warning' : 'success'" size="small" effect="dark">
              {{ scope.row.status === 0 ? $t('common.cancelled') : scope.row.status === 2 ? $t('common.frozen') : $t('common.inUse') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createDate" :label="$t('common.createdAt')">
          <template slot-scope="scope">
            {{ formatDate(scope.row.createDate) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.actions')" width="160" fixed="right" align="center">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button
                type="primary"
                size="mini"
                plain
                @click="handleViewUserDetail(scope.row.userid)"
                icon="el-icon-view"
                :aria-label="$t('common.detail')"
              >
                {{ $t('common.detail') }}
              </el-button>
              <el-button
                type="danger"
                size="mini"
                plain
                @click="handleRemoveUser(scope.row.userid)"
                icon="el-icon-delete"
                :aria-label="$t('common.remove')"
              >
                {{ $t('common.remove') }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 批量操作 -->
      <div v-if="selectedUsers.length > 0" class="batch-operations">
        <span>{{ $t('userManagement.selectedCount', { count: selectedUsers.length }) }}</span>
        <el-button type="danger" @click="handleBatchRemoveUsers">
          {{ $t('userManagement.removeSelected') }}
        </el-button>
      </div>
    </el-card>

    <!-- 添加用户对话框 -->
    <el-dialog
      :title="$t('userManagement.addUsersToGroup')"
      :visible.sync="addUsersDialogVisible"
      width="800px"
      :close-on-click-modal="false"
      append-to-body
    >
      <user-group-add-users
        v-if="addUsersDialogVisible"
        :group-id="currentGroup.id"
        @success="handleAddUsersSuccess"
        @close="addUsersDialogVisible = false"
      />
    </el-dialog>
  </div>
</template>

<script>
import UserGroupAddUsers from './UserGroupAddUsers.vue'

export default {
  name: 'UserGroupDetail',
  components: {
    UserGroupAddUsers
  },
  data() {
    return {
      selectedUsers: [],
      addUsersDialogVisible: false,
      loading: false
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    currentGroup() {
      const groupId = this.$route.params.id
      const group = this.$store.getters.userGroupById(groupId)
      return group || { id: groupId, name: '', description: '', user_ids: [], createDate: '' }
    },
    groupUsers() {
      const allUsers = this.$store.getters.allUsers
      const userIds = this.currentGroup.user_ids || []
      
      // If we have user_ids, try to get full objects from the store's global users list
      if (userIds.length > 0 && allUsers && allUsers.length > 0) {
        const users = allUsers.filter(user => userIds.some(id => String(id) === String(user.userid)))
        // If we found all users in allUsers, return them as they are the most likely to be up-to-date
        if (users.length === userIds.length) {
          return users
        }
      }

      // Fallback: prioritize the 'users' field from the group object if available (populated by backend)
      // but always filter it by user_ids to ensure consistency
      if (this.currentGroup.users && this.currentGroup.users.length > 0) {
        if (userIds.length > 0) {
          return this.currentGroup.users.filter(user => userIds.some(id => String(id) === String(user.userid)))
        }
        return this.currentGroup.users
      }
      
      // Last resort: return whatever we found in allUsers
      if (userIds.length > 0 && allUsers && allUsers.length > 0) {
        return allUsers.filter(user => userIds.some(id => String(id) === String(user.userid)))
      }
      
      return []
    }
  },
  methods: {
    async fetchData() {
      const groupId = this.$route.params.id
      if (groupId) {
        this.loading = true
        try {
          await this.$store.dispatch('fetchUserGroup', groupId)
          
          // If the group doesn't have populated users, ensure relevant users are loaded for fallback
          if (!this.currentGroup.users || this.currentGroup.users.length === 0) {
            const allUsers = this.$store.getters.allUsers
            const userIds = this.currentGroup.user_ids || []
            const missingSome = userIds.some(id => !allUsers.some(u => String(u.userid) === String(id)))
            
            if (missingSome || allUsers.length === 0) {
              await this.$store.dispatch('fetchUsers', { page: 1, limit: 1000 })
            }
          }
        } catch (error) {
          console.error('Failed to fetch group details:', error)
          this.$message.error(this.$t('common.fetchError'))
        } finally {
          this.loading = false
        }
      }
    },

    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString()
    },
    
    // 返回上一页
    handleBack() {
      const redirect = this.$route.query.redirect
      if (redirect) {
        this.$router.push(redirect)
      } else {
        this.$router.go(-1)
      }
    },
    
    // 编辑分组
    handleEdit() {
      this.$router.push(`/user/group/edit/${this.currentGroup.id}`)
    },
    
    // 添加用户
    handleAddUsers() {
      console.log('handleAddUsers clicked, currentGroup:', this.currentGroup)
      this.addUsersDialogVisible = true
    },
    
    // 添加用户成功回调
    handleAddUsersSuccess(data) {
      if (data.success) {
        this.addUsersDialogVisible = false
        this.fetchData() // 刷新数据
      }
    },
    
    // 查看用户详情
    handleViewUserDetail(userId) {
      this.$router.push({ name: 'UserDetail', params: { id: userId } })
    },
    
    // 移除用户
    handleRemoveUser(userId) {
      const user = this.$store.getters.userById(userId) || this.groupUsers.find(u => String(u.userid) === String(userId))
      const displayName = user ? (user.name || user.username) : userId
      
      this.$confirm(
        this.$t('userManagement.removeUserFromGroupConfirm', { username: displayName }),
        this.$t('common.confirm'),
        {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        }
      ).then(() => {
        // 更新分组用户
        const newUserIds = this.currentGroup.user_ids
          .filter(id => String(id) !== String(userId))
          .map(id => Number(id))
          
        this.$store.dispatch('updateUserGroup', {
          id: Number(this.currentGroup.id),
          user_ids: newUserIds
        }).then(() => {
          this.$message.success(this.$t('userManagement.removeUserSuccess'))
          this.fetchData() // Refresh data
        }).catch(err => {
          this.$message.error(this.$t('userManagement.removeUserError'))
        })
      }).catch(() => {
        this.$message.info(this.$t('common.cancel'))
      })
    },
    
    // 批量移除用户
    handleBatchRemoveUsers() {
      this.$confirm(
        this.$t('userManagement.batchRemoveUsersConfirm', { count: this.selectedUsers.length }),
        this.$t('common.confirm'),
        {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        }
      ).then(() => {
        const selectedUserIds = this.selectedUsers.map(user => String(user.userid))
        const newUserIds = this.currentGroup.user_ids
          .filter(id => !selectedUserIds.includes(String(id)))
          .map(id => Number(id))
        
        this.$store.dispatch('updateUserGroup', {
          id: Number(this.currentGroup.id),
          user_ids: newUserIds
        }).then(() => {
          this.$message.success(this.$t('userManagement.batchRemoveUsersSuccess'))
          this.selectedUsers = []
          this.fetchData() // Refresh data
        }).catch(err => {
          this.$message.error(this.$t('userManagement.batchRemoveUsersError'))
        })
      }).catch(() => {
        this.$message.info(this.$t('common.cancel'))
      })
    },
    
    // 用户状态变更
    handleUserSelectionChange(val) {
      this.selectedUsers = val
    }
  }
}
</script>

<style scoped lang="scss">
.user-group-detail-container {
  padding: 0;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;

  /* 隐藏滚动条 */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */

  .control-panel {
    margin-bottom: 24px;
    
    .control-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
    }

    .header-info {
      display: flex;
      align-items: center;
      gap: 10px;
      color: #606266;
      font-size: 14px;
      
      i {
        font-size: 18px;
        color: #409eff;
      }
    }

    .action-wrapper {
      display: flex;
      align-items: center;
      gap: 12px;
    }
  }

  .user-group-detail-info,
  .user-group-detail-users {
    margin-bottom: 20px;
    border-radius: 8px;
    border: 1px solid #ebeef5;
    transition: all 0.3s;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    span {
      font-weight: bold;
      font-size: 16px;
    }
  }

  .group-header-content {
    display: flex;
    align-items: flex-start;
    padding: 10px 0;

    .group-icon-wrapper {
      margin-right: 24px;
      .group-icon {
        font-size: 56px;
        color: #409EFF;
        background: #ecf5ff;
        padding: 16px;
        border-radius: 16px;
      }
    }

    .group-main-info {
      flex: 1;

      .name-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;

        .group-name-text {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
          color: #303133;
        }
      }

      .group-desc-text {
        font-size: 14px;
        color: #909399;
        margin-bottom: 16px;
        line-height: 1.5;
      }

      .group-meta-info {
        display: flex;
        align-items: center;
        gap: 20px;

        .meta-info-item {
          display: flex;
          align-items: center;
          font-size: 13px;
          color: #606266;

          i {
            margin-right: 6px;
            color: #909399;
          }

          .label-text {
            margin-right: 8px;
          }

          .value-text {
            color: #303133;
            font-weight: 500;
          }
        }

        .meta-info-divider {
          width: 1px;
          height: 14px;
          background: #dcdfe6;
        }
      }
    }
  }

  .user-group-detail-info {
    .el-card__body {
      padding: 24px;
    }
  }

  .batch-operations {
    margin-top: 20px;
    text-align: right;

    span {
      margin-right: 20px;
    }
  }

  .action-buttons {
    display: flex;
    justify-content: center;
    gap: 8px;
  }

  .table-operations {
    margin-bottom: 15px;
  }
}

/* 统一滚动条样式 */
:deep(.el-table__body-wrapper) {
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #dbdbdb;
    border-radius: 3px;
    &:hover {
      background: #c0c0c0;
    }
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

@media screen and (max-width: 1400px) {
  .action-buttons {
    gap: 4px;
  }
  
  .action-buttons .el-button--mini {
    padding: 7px 10px;
    min-width: 60px;
  }
}

@media screen and (max-width: 768px) {
  .user-group-detail-container :deep(.el-table__fixed-right) {
    height: 100% !important;
  }
  
  .action-buttons {
    flex-direction: row;
    justify-content: center;
    gap: 4px;
  }

  .action-buttons .el-button--mini {
    padding: 6px 8px;
    min-width: 50px;
    font-size: 11px;
  }
}
</style>